import { createClient } from '@supabase/supabase-js';

import { env } from '@web/utils/env';

// Single browser-side Supabase client. Uses the publishable anon key.
// Auth/session handling is OURS (custom JWT signed by edge functions
// using the project JWT secret) — supabase-js auto-refresh is disabled.
//
// Every outgoing request gets the user's bearer token attached via the
// `fetch` hook, which dynamically imports the auth bearer module to
// avoid the procedures.ts <-> supabase.ts <-> auth/bearer.ts <->
// auth/service.ts <-> trpcClient.ts <-> procedures.ts import cycle.
export const supabase = createClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
  global: {
    fetch: async (url, options) => {
      const init = options ?? {};
      const headers = new Headers(init.headers);
      // The unauthenticated edge functions (verify_jwt=false) must NOT go
      // through getBearerToken(). auth-refresh in particular is invoked from
      // *inside* getBearerToken()'s in-flight promise (expired access token →
      // refreshAuth() → this fetch). Re-entering getBearerToken() there returns
      // the same unresolved promise, which awaits refreshAuth, which awaits this
      // fetch → permanent deadlock that hangs the splash screen forever. These
      // endpoints need no token anyway, so skip the bearer entirely.
      const href = typeof url === 'string' ? url : (url as URL).toString();
      const isPublicAuthFn = /\/functions\/v1\/auth-(nonce|siwe|refresh)\b/.test(
        href,
      );
      if (!isPublicAuthFn) {
        const { getBearerToken } = await import('@web/utils/auth/bearer');
        const token = await getBearerToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
      }
      return fetch(url, { ...init, headers });
    },
  },
});
